/*
 * The rules behind a page's chrome, kept apart from its components so they can be checked without
 * a browser: where an in-list sticky header sits, and what the browser tab calls the page.
 */

/** The chrome that sticks to the top of the page scroll: the PageHeader, and a PageToolbar under it. */
export type ChromeLayer = "header" | "toolbar"

/**
 * Where an in-list sticky header (a table's head, a GroupHeader, a ticket's rail) sticks: under the
 * 48px PageHeader, or under the 40px PageToolbar too when the page has one. Written out whole so
 * Tailwind finds both classes.
 */
export const stickyTop = { header: "top-12", toolbar: "top-22" } as const

export type StickyTop = (typeof stickyTop)[ChromeLayer]

/** How many of each layer are mounted on the page right now. */
export type MountedChrome = Record<ChromeLayer, number>

/** The sticky offset for what is mounted: below the toolbar when there is one, else the header. */
export function stickyTopOf(mounted: MountedChrome): StickyTop {
  return mounted.toolbar > 0 ? stickyTop.toolbar : stickyTop.header
}

/**
 * What the browser tab calls a page: its crumbs, most specific first, then Beacon — "Members ·
 * Beacon", "Settings · Operations · Projects · Beacon". A page with a better name for itself than
 * its crumbs, such as a ticket's key and title, passes it as `documentTitle`, and Beacon follows.
 */
export function documentTitleOf(crumbs: readonly { label: string }[], documentTitle?: string) {
  const specific = documentTitle ? [documentTitle] : crumbs.map(({ label }) => label).reverse()
  return [...specific, "Beacon"].join(" · ")
}
